import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { ArrowLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBottomRightExclusion } from "@/components/layout/useBottomRightExclusion";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { WidgetField, WidgetGroup } from "@/domains/buildings/types/buildingWidget";
import { groupHistoryEntriesByDay } from "@/domains/documents/history/documentHistoryPresentation";
import {
  useBuildingDataHistory,
  type BuildingDataHistoryEntry,
} from "@/hooks/buildings/useBuildingDataHistory";
import { useResizablePanelWidth } from "@/hooks/useResizablePanelWidth";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { BuildingDataWidgetSections } from "@/lib/i18n/types/buildings-sections/building-data-widget";
import { AttributeHistoryContent } from "./AttributeHistoryModal";
import { ActorSummary, EditAvatar } from "./HistoryTimelineItem";

/**
 * Building-wide value-history panel: every user edit across all attributes of
 * the building, newest-first, from the append-only `data.user_data` history
 * (fetched lazily on open via `get_building_data_history`). Each node shows
 * when, which attribute, the value it was set to, and who set it. Machine
 * sources (EHR / extractor / IFC) keep no history — their current snapshots
 * live in each row's tooltip and the per-attribute history view.
 *
 * Clicking an entry drills into that attribute's history in the same panel;
 * the back arrow returns to the building-wide timeline.
 */

// v1 → v2: the old hook auto-wrote a viewport-sized width 200ms after mount,
// which the new hook would read back as a deliberate preference and pin,
// disabling the responsive default below. A new key discards those.
const HISTORY_PANEL_STORAGE_KEY = "building-data-history-panel:v2";
const DEFAULT_WIDTH_RATIO = 0.35;

interface BuildingDataHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buildingId: string | undefined;
  orgId: string | undefined;
  /** Widget groups already loaded by the page — used to resolve attribute labels/units. */
  groups: WidgetGroup[];
  /** Registry code + doc-preview opener, forwarded to the drilled-into attribute modal. */
  registryCode?: string | null;
  onDocPreview: (filename: string) => void;
  returnFocusRef: RefObject<HTMLButtonElement>;
}

/** Fallback label for attributes not on the widget: "fire_alarm_type" → "Fire Alarm Type". */
function titleCaseKey(key: string): string {
  return key.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function HistorySkeleton(props: { label: string }) {
  return (
    <div className="divide-y" role="status" aria-label={props.label}>
      {Array.from({ length: 4 }, (_, index) => (
        <div key={index} className="flex gap-3 px-5 py-4">
          <Skeleton className="h-9 w-9 flex-none rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex justify-between gap-4">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-14" />
            </div>
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function HistoryMessage(props: { message: string; role: "status" | "alert" }) {
  return (
    <div className="flex min-h-48 items-center justify-center px-8 py-10 text-center">
      <p className="text-sm text-muted-foreground" role={props.role}>
        {props.message}
      </p>
    </div>
  );
}

interface BuildingHistoryRowProps {
  entry: BuildingDataHistoryEntry;
  field: WidgetField | undefined;
  language: string;
  copy: BuildingDataWidgetSections;
  relativeTimeCopy: ReturnType<typeof useLanguage>["t"]["documents"]["log"]["history"];
  onOpen: (() => void) | undefined;
}

function attributeLabel(field: WidgetField | undefined, attributeKey: string): string {
  return field?.label ?? titleCaseKey(attributeKey);
}

function AttributeValue(props: Pick<BuildingHistoryRowProps, "entry" | "field">) {
  return (
    <p className="mt-1 break-words text-xs text-muted-foreground">
      {props.entry.attributeValue || "—"}
      {props.field?.unit ? <span className="ml-1">{props.field.unit}</span> : null}
    </p>
  );
}

function AttributeSummary(props: {
  entry: BuildingDataHistoryEntry;
  field: WidgetField | undefined;
  label: string;
  interactive: boolean;
}) {
  return (
    <div className="mt-2 flex min-w-0 items-center gap-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{props.label}</p>
        <AttributeValue entry={props.entry} field={props.field} />
      </div>
      {props.interactive ? (
        <ChevronRight className="h-4 w-4 flex-none text-muted-foreground" aria-hidden="true" />
      ) : null}
    </div>
  );
}

function BuildingHistoryRowContent(props: BuildingHistoryRowProps & { label: string }) {
  return (
    <>
      <EditAvatar entry={props.entry} unknownUser={props.copy.unknownUser} />
      <div className="min-w-0 flex-1">
        <ActorSummary
          entry={props.entry}
          language={props.language}
          unknownUser={props.copy.unknownUser}
          relativeTimeCopy={props.relativeTimeCopy}
        />
        <AttributeSummary
          entry={props.entry}
          field={props.field}
          label={props.label}
          interactive={Boolean(props.onOpen)}
        />
      </div>
    </>
  );
}

function BuildingHistoryRow(props: BuildingHistoryRowProps) {
  const label = attributeLabel(props.field, props.entry.attributeKey);
  const content = <BuildingHistoryRowContent {...props} label={label} />;

  if (!props.onOpen) return <div className="flex gap-3 px-5 py-4">{content}</div>;
  return (
    <button
      type="button"
      className="flex w-full gap-3 px-5 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      onClick={props.onOpen}
      aria-label={`${props.copy.viewHistory}: ${label}`}
    >
      {content}
    </button>
  );
}

function HistoryRows(props: {
  entries: BuildingDataHistoryEntry[];
  fieldByKey: Map<string, WidgetField>;
  language: string;
  copy: BuildingDataWidgetSections;
  documentCopy: ReturnType<typeof useLanguage>["t"]["documents"]["log"]["history"];
  onOpen: (attributeKey: string) => void;
}) {
  const groups = groupHistoryEntriesByDay(
    props.entries,
    props.language,
    props.documentCopy.dayGroups
  );
  return (
    <div role="list" aria-label={props.copy.buildingDataHistory}>
      {groups.map((group) => (
        <section key={group.key} aria-labelledby={`building-data-history-day-${group.key}`}>
          <h3
            id={`building-data-history-day-${group.key}`}
            className="sticky top-0 z-20 border-b bg-background/95 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur"
          >
            {group.label}
          </h3>
          <ul>
            {group.entries.map((entry, index) => {
              const field = props.fieldByKey.get(entry.attributeKey);
              return (
                <li
                  key={`${entry.attributeKey}-${entry.createdAt}-${index}`}
                  className="border-b transition-colors last:border-b-0 focus-within:bg-[#f7f7f7] hover:bg-[#f7f7f7]"
                >
                  <BuildingHistoryRow
                    entry={entry}
                    field={field}
                    language={props.language}
                    copy={props.copy}
                    relativeTimeCopy={props.documentCopy}
                    onOpen={field ? () => props.onOpen(entry.attributeKey) : undefined}
                  />
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

type BuildingHistoryQuery = ReturnType<typeof useBuildingDataHistory>;

function HistoryBody(props: {
  history: BuildingHistoryQuery;
  entries: BuildingDataHistoryEntry[];
  fieldByKey: Map<string, WidgetField>;
  language: string;
  copy: BuildingDataWidgetSections;
  documentCopy: ReturnType<typeof useLanguage>["t"]["documents"]["log"]["history"];
  onOpen: (attributeKey: string) => void;
}) {
  if (props.history.isLoading) return <HistorySkeleton label={props.copy.loadingHistory} />;
  if (props.history.isError)
    return <HistoryMessage message={props.copy.historyLoadError} role="alert" />;
  if (props.entries.length === 0)
    return <HistoryMessage message={props.copy.noHistory} role="status" />;
  return (
    <HistoryRows
      entries={props.entries}
      fieldByKey={props.fieldByKey}
      language={props.language}
      copy={props.copy}
      documentCopy={props.documentCopy}
      onOpen={props.onOpen}
    />
  );
}

function HistoryFooter(props: {
  count: number;
  copy: ReturnType<typeof useLanguage>["t"]["documents"]["log"]["history"];
}) {
  if (props.count === 0) return null;
  const count = String(props.count);
  return (
    <footer className="border-t bg-background px-5 py-3">
      <span className="text-xs font-medium tabular-nums text-muted-foreground">
        {props.copy.eventCount.replace("{shown}", count).replace("{total}", count)}
      </span>
    </footer>
  );
}

function BuildingHistoryOverview(props: {
  history: BuildingHistoryQuery;
  fieldByKey: Map<string, WidgetField>;
  language: string;
  copy: BuildingDataWidgetSections;
  documentCopy: ReturnType<typeof useLanguage>["t"]["documents"]["log"]["history"];
  onOpenEntry: (attributeKey: string) => void;
}) {
  const entries = props.history.data ?? [];
  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto" aria-busy={props.history.isFetching}>
        <HistoryBody
          history={props.history}
          entries={entries}
          fieldByKey={props.fieldByKey}
          language={props.language}
          copy={props.copy}
          documentCopy={props.documentCopy}
          onOpen={props.onOpenEntry}
        />
      </div>
      <HistoryFooter count={entries.length} copy={props.documentCopy} />
    </>
  );
}

function usePanelFocus(open: boolean, returnFocusRef: RefObject<HTMLButtonElement>) {
  const panelRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!open) return;
    const returnFocus = returnFocusRef.current;
    panelRef.current?.focus();
    return () => {
      if (returnFocus?.isConnected) returnFocus.focus();
    };
  }, [open, returnFocusRef]);
  return panelRef;
}

function useEscapeClose(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const modalDialog = document.querySelector('[role="dialog"][aria-modal="true"]');
      if (modalDialog) return;
      onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open, onClose]);
}

type PanelResize = ReturnType<typeof useResizablePanelWidth>;

function ResizeHandle(props: { resize: PanelResize; label: string }) {
  const active = props.resize.dragging || props.resize.hover;
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={props.label}
      {...props.resize.handleProps}
      className="absolute bottom-0 left-0 top-0 z-40 w-1.5 cursor-col-resize touch-none transition-colors"
      style={{ background: active ? "hsl(var(--primary) / 0.4)" : "transparent" }}
    />
  );
}

interface PanelHeaderProps {
  title: string;
  subtitle: string;
  copy: BuildingDataWidgetSections;
  onBack?: () => void;
  onClose: () => void;
}

function PanelHeader(props: PanelHeaderProps) {
  return (
    <header className="flex flex-shrink-0 items-start justify-between gap-4 px-5 py-4">
      <div className="flex min-w-0 items-start gap-2">
        {props.onBack ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-none text-muted-foreground hover:text-foreground"
                aria-label={props.copy.backToHistory}
                onClick={props.onBack}
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{props.copy.backToHistory}</TooltipContent>
          </Tooltip>
        ) : null}
        <div className="min-w-0">
          <h2
            id="building-data-history-title"
            className="truncate text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
          >
            {props.title}
          </h2>
          <p id="building-data-history-subtitle" className="mt-1 truncate text-sm text-foreground">
            {props.subtitle}
          </p>
        </div>
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-none text-muted-foreground hover:text-foreground"
            aria-label={props.copy.closeHistory}
            onClick={props.onClose}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{props.copy.closeHistory}</TooltipContent>
      </Tooltip>
    </header>
  );
}

interface HistoryPanelProps {
  resize: PanelResize;
  title: string;
  subtitle: string;
  copy: BuildingDataWidgetSections;
  onBack?: () => void;
  onClose: () => void;
  children: ReactNode;
}

const HistoryPanel = forwardRef<HTMLElement, HistoryPanelProps>(function HistoryPanel(props, ref) {
  return (
    <aside
      ref={ref}
      tabIndex={-1}
      role="dialog"
      aria-modal="false"
      aria-labelledby="building-data-history-title"
      aria-describedby="building-data-history-subtitle"
      data-building-data-history="true"
      className="building-data-widget fixed bottom-0 right-0 top-0 z-30 flex flex-col overflow-hidden border-l bg-background"
      style={{ width: props.resize.width, boxShadow: "-18px 0 40px -24px rgba(40,30,15,0.22)" }}
    >
      <ResizeHandle resize={props.resize} label={props.copy.resizeHistoryPanel} />
      <PanelHeader
        title={props.title}
        subtitle={props.subtitle}
        copy={props.copy}
        onBack={props.onBack}
        onClose={props.onClose}
      />
      {props.children}
    </aside>
  );
});

function indexFields(groups: WidgetGroup[]): Map<string, WidgetField> {
  const fields = new Map<string, WidgetField>();
  for (const group of groups) {
    for (const field of group.fields) fields.set(field.id, field);
  }
  return fields;
}

function useFieldIndex(groups: WidgetGroup[]): Map<string, WidgetField> {
  return useMemo(() => indexFields(groups), [groups]);
}

function useHistoryDrilldown() {
  const [key, setKey] = useState<string | null>(null);
  return { key, open: setKey, close: () => setKey(null) };
}

export function BuildingDataHistoryModal({
  open,
  onOpenChange,
  buildingId,
  orgId,
  groups,
  registryCode,
  onDocPreview,
  returnFocusRef,
}: BuildingDataHistoryModalProps) {
  const { t, language } = useLanguage();
  const tw = t.buildings.buildingDataWidget;
  const documentHistoryCopy = t.documents.log.history;
  const history = useBuildingDataHistory(buildingId, orgId, open);
  const fieldByKey = useFieldIndex(groups);
  const drilldown = useHistoryDrilldown();
  const resize = useResizablePanelWidth(
    HISTORY_PANEL_STORAGE_KEY,
    Math.round((typeof window !== "undefined" ? window.innerWidth : 1440) * DEFAULT_WIDTH_RATIO)
  );
  useBottomRightExclusion("building-data-history-panel", resize.settledWidth, open);
  const panelRef = usePanelFocus(open, returnFocusRef);
  const close = () => {
    drilldown.close();
    onOpenChange(false);
  };
  useEscapeClose(open, close);

  if (!open) return null;
  const drillField = drilldown.key ? fieldByKey.get(drilldown.key) : undefined;

  return (
    <TooltipProvider>
      <HistoryPanel
        ref={panelRef}
        resize={resize}
        title={drillField?.label ?? tw.buildingDataHistory}
        subtitle={tw.valueHistory}
        copy={tw}
        onBack={drillField ? drilldown.close : undefined}
        onClose={close}
      >
        {drillField ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-2">
            <AttributeHistoryContent
              field={drillField}
              buildingId={buildingId}
              orgId={orgId}
              registryCode={registryCode}
              onDocPreview={onDocPreview}
              enabled={open}
            />
          </div>
        ) : (
          <BuildingHistoryOverview
            history={history}
            fieldByKey={fieldByKey}
            language={language}
            copy={tw}
            documentCopy={documentHistoryCopy}
            onOpenEntry={drilldown.open}
          />
        )}
      </HistoryPanel>
    </TooltipProvider>
  );
}
